import React, { createContext, useContext, useState, useEffect } from 'react';

const UserContext = createContext(null);
const API = process.env.REACT_APP_API_URL || '';

const sessionAuthEnabled = () => process.env.REACT_APP_SESSION_AUTH === 'true';
const legacyLoginAllowed = () => process.env.REACT_APP_ALLOW_LEGACY_LOGIN !== 'false';

const shapeSessionUser = (session) => {
  const { csrf_token: _csrfToken, ...safeSession } = session;
  const dynasty = session.memberships?.find(m => m.kind === 'dynasty');
  return {
    ...safeSession,
    display_name: session.display_name,
    owner_id: dynasty?.owner_id || null,
    roster_id: dynasty?.roster_id || null,
    memberships: session.memberships || [],
    session_authenticated: true,
  };
};

const loadLegacyUser = () => {
  const saved = localStorage.getItem('dynasty_user');
  if (!saved) return null;
  try {
    return JSON.parse(saved);
  } catch {
    localStorage.removeItem('dynasty_user');
    return null;
  }
};

export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    if (sessionAuthEnabled()) {
      const bootstrap = async () => {
        const params = new URLSearchParams(window.location.search);
        const token = window.location.pathname === '/auth/redeem' ? params.get('token') : null;
        try {
          if (token) {
            const redeemed = await fetch(`${API}/api/auth/redeem`, {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token }),
            });
            window.history.replaceState({}, '', '/');
            if (!redeemed.ok) throw new Error('Invitation is invalid or expired');
          }
          const response = await fetch(`${API}/api/auth/session`, { credentials: 'include' });
          const session = await response.json();
          if (session.authenticated) {
            localStorage.removeItem('dynasty_user');
            setCsrfToken(session.csrf_token || '');
            setCurrentUser(shapeSessionUser(session));
          } else if (legacyLoginAllowed()) {
            setCurrentUser(loadLegacyUser());
          } else {
            localStorage.removeItem('dynasty_user');
            setCurrentUser(null);
          }
        } catch {
          setCurrentUser(null);
        } finally {
          setLoadingUser(false);
        }
      };
      bootstrap();
      return;
    }
    if (legacyLoginAllowed()) {
      setCurrentUser(loadLegacyUser());
    } else {
      localStorage.removeItem('dynasty_user');
      setCurrentUser(null);
    }
    setLoadingUser(false);
  }, []);

  const login = (userData) => {
    if (!legacyLoginAllowed() && !userData?.guest) return false;
    localStorage.setItem('dynasty_user', JSON.stringify(userData));
    setCurrentUser(userData);
    return true;
  };

  const refreshCsrf = async () => {
    if (!sessionAuthEnabled()) return '';
    try {
      const response = await fetch(`${API}/api/auth/session`, { credentials: 'include' });
      const session = await response.json();
      if (!response.ok || !session.authenticated) return '';
      const token = session.csrf_token || '';
      setCurrentUser(shapeSessionUser(session));
      setCsrfToken(token);
      localStorage.removeItem('dynasty_user');
      return token;
    } catch {
      return '';
    }
  };

  const logout = async () => {
    if (currentUser?.session_authenticated) {
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST', credentials: 'include',
        headers: { 'X-CSRF-Token': csrfToken },
      }).catch(() => {});
    }
    localStorage.removeItem('dynasty_user');
    setCsrfToken('');
    setCurrentUser(null);
  };

  return (
    <UserContext.Provider value={{ currentUser, loadingUser, csrfToken, refreshCsrf, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
